import { FormControl, Tooltip } from '@mui/material';
import { JSX } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../../store';
import { getNameWithCode } from '../../services';
import {
  SingleValueSelectControl,
  MultiValueSelectControl,
  StringControl,
  SubModelControl,
  DateControl,
  NumberControl,
} from '..';
import { InfoDialogIcon } from './InfoDialogIcon';
import {
  checkIsOptionReadonly,
  getLabelWithInCompleteIcon,
  getPropertyValue,
  isReadOnlyAccess,
  isRestrictedState,
  isVariableVisible,
} from '../../services/ConfigurationDataHelperFunctions';
import { EVariableType, EConfigitProperty, ESectionInfoType } from '../../data/Constants';
import { AppStore } from '../../../src/store';
import type { IFeatureOrFamily } from '../../../types/IHelperTypes';
import type {
  IValueType,
  IVariableInputProps,
  IExtendedConfigureResponse,
  IConfigurationVariable,
  IConfigurationValue,
} from '../../../types';

/**
 * Resolves the display value for a numeric variable, applying optional-numeric precedence rules.
 * Stored optional value wins over configure response, except when ByRule inference applies to
 * a non-user-assigned variable (SM4). User-assigned origin (SM1) is always protected.
 */
function resolveOptionalNumericValue(
  variable: IConfigurationVariable,
  singletonValue: IConfigurationValue | undefined,
  config: IExtendedConfigureResponse
): string {
  const optionalStoredVal =
    variable.variableType === EVariableType.Number
      ? config.savedConfiguration?.optionalItems?.get(variable.id)
      : undefined;
  // Stored optionalItems value is always authoritative for numeric optional; SM4 keeps it in sync with rule changes.
  if (optionalStoredVal !== null && typeof optionalStoredVal === 'object') {
    return optionalStoredVal.value;
  }
  return (singletonValue ? singletonValue.value : '') as string;
}

/**
 * Extracts the feature name from a label that follows the pattern "featureName (code)".
 * Uses regex to parse labels and return only the feature name portion before the parentheses.
 *
 * @param {string} label - The input label string to parse, expected format: "featureName (anything)"
 * @returns {string} The extracted feature name if pattern matches, otherwise returns the original label
 */
function excludeCode(label: string): string {
  // Match pattern: "featureName (anything)"
  const regex = /^(.*?)(?:\s*\([^()]*\)\s*)$/;
  const match = regex.exec(label);
  return match ? match[1] : label;
}

/**
 * Renders the input component for the given variable.
 * Decides based on the variable which input to use.
 * Handles readonly mode and showCode/hideCodes functionality
 * @param {IVariableInputProps} props the properties for the dynamic input component
 * @returns {JSX.Element} the most suitable input for the given variable
 */

export const DynamicInputControl = (props: IVariableInputProps) => {
  const { variable, type, onChange, onOptionalChange } = props;
  const showCode = useSelector((state: AppState) => state.userSettings.showCode);
  const featureFlags = AppStore.getState().claimsData.featureFlags;

  const isGuarded = variable.isGuarded;
  const config: IExtendedConfigureResponse = AppStore.getState().configuration;

  const disableInputControl = (): boolean => {
    const savedState = config.savedConfiguration?.state;
    const accessState = config.access;
    return (
      isRestrictedState(savedState) ||
      isReadOnlyAccess(accessState) ||
      isGuarded ||
      checkIsOptionReadonly(variable) ||
      variable.isReadOnly ||
      !!variable.bundleInfo?.isBundled ||
      getPropertyValue<boolean>(
        variable,
        EConfigitProperty.IsPasswordProtected,
        EVariableType.Boolean
      )
    );
  };

  const disabled: boolean = disableInputControl();
  const variableType = type === '' ? variable.variableType : type;
  const singletonValue = variable.values?.find((v) => v.assigned);
  const value = resolveOptionalNumericValue(variable, singletonValue, config);
  const label = getNameWithCode(variable as IFeatureOrFamily, showCode);
  const legend =
    variableType === EVariableType.Number || variableType === EVariableType.Submodel
      ? excludeCode(label)
      : label;

  /*
    If hidden/suppressed return null. Return null will not show any input control for particular variable in UI.
    It doesn't have any impact on configuration summary, price or any other functionality of application
  */

  if (!isVariableVisible(variable)) {
    return null;
  }

  const onInternalChange = (v: IValueType) => onChange?.(variable, v);

  const renderFormControl = (component: JSX.Element) => {
    return (
      <fieldset
        className={
          variable.isSearchValueMatched ? 'inputFieldsetWithSearchValueMatched' : 'inputFieldset'
        }
      >
        <legend className="inputLegend">
          <Tooltip
            title={legend}
            placement="right-start"
            slotProps={{
              popper: {
                className: 'dynamic-input-tooltip',
              },
            }}
          >
            {getLabelWithInCompleteIcon(legend, variable.complete ?? true)}
          </Tooltip>
          {type !== EVariableType.Number && type !== EVariableType.Submodel && (
            <InfoDialogIcon variable={variable as IFeatureOrFamily} type="FF" />
          )}
        </legend>

        <FormControl fullWidth variant="outlined" disabled={disabled}>
          {component}
        </FormControl>
      </fieldset>
    );
  };

  switch (variableType) {
    case EVariableType.Singleselect:
      return renderFormControl(
        <SingleValueSelectControl
          featureFlags={featureFlags}
          key={variable.id}
          variable={variable}
          label={label}
          values={variable.values}
          value={value}
          onChange={onInternalChange}
          onOptionalChange={onOptionalChange}
          disabled={disabled}
          showCode={showCode}
        />
      );
    case EVariableType.Multiselect:
      return renderFormControl(
        <MultiValueSelectControl
          featureFlags={featureFlags}
          key={variable.id}
          variable={variable}
          label={label}
          values={variable.values}
          onChange={onInternalChange}
          onOptionalChange={onOptionalChange}
          disabled={disabled}
          showCode={showCode}
        />
      );
    case ESectionInfoType.MultiNumeric:
      return (
        <NumberControl
          featureFlags={featureFlags}
          key={variable.id}
          variable={variable}
          value={value}
          disabled={disabled}
          onChange={onInternalChange}
          onOptionalChange={onOptionalChange}
        />
      );
    case EVariableType.Number:
      return renderFormControl(
        <NumberControl
          featureFlags={featureFlags}
          key={variable.id}
          variable={variable}
          value={value}
          disabled={disabled}
          onChange={onInternalChange}
          onOptionalChange={onOptionalChange}
        />
      );
    case EVariableType.Submodel:
      return renderFormControl(
        <SubModelControl
          key={variable.id}
          variable={variable}
          label={label}
          value={value}
          onChange={onInternalChange}
          disabled={!!disabled}
        />
      );
    case EVariableType.Date:
      return renderFormControl(
        <DateControl
          featureFlags={featureFlags}
          key={variable.id}
          variable={variable}
          label={label}
          value={value}
          onChange={onInternalChange}
          onOptionalChange={onOptionalChange}
          disabled={disabled}
        />
      );
    default:
      return renderFormControl(
        <StringControl
          key={variable.id}
          variable={variable}
          label={label}
          value={value}
          disabled={disabled}
          onChange={onInternalChange}
        />
      );
  }
};
